import React, { useState, useEffect } from "react";
import { View, Text, TextInput, Switch, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { Ionicons, FontAwesome6 } from "@expo/vector-icons";
import { useTheme } from "../context/ThemeContext";
import { useComponentEditor } from "../context/ComponentEditorContext";
import { useDesign } from "../context/DesignContext";
import { useContent } from "../context/ContentContext";
import { useProject } from "../context/ProjectContext";
import EditorModal, { MODAL_MODES } from "../pages/modals/EditorModal";

// Common icon options that can be used across components
const COMMON_ICONS = [
	"add-outline",
	"create-outline",
	"document-outline",
	"book-outline",
	"bookmark-outline",
	"star-outline",
	"heart-outline",
	"ribbon-outline",
	"trophy-outline",
	"settings-outline",
	"people-outline",
	"person-outline",
	"calendar-outline",
	"images-outline",
	"image-outline",
	"camera-outline",
	"videocam-outline",
	"musical-notes-outline",
	"chatbox-outline",
	"mail-outline",
	"home-outline",
	"globe-outline",
];

// Icon selector component that can be reused
const IconSelector = ({ value, onChange, onClose, index }) => {
	const [searchQuery, setSearchQuery] = useState("");

	// Filter icons based on search query
	const filteredIcons = searchQuery ? COMMON_ICONS.filter((icon) => icon.toLowerCase().includes(searchQuery.toLowerCase())) : COMMON_ICONS;

	return (
		<View style={[styles.iconSelectorContainer, { zIndex: index }]}>
			<View style={styles.iconSelectorHeader}>
				<Text style={styles.iconSelectorTitle}>Select Icon</Text>
				<TouchableOpacity onPress={onClose} style={styles.iconSelectorCloseBtn}>
					<Ionicons name="close" size={18} color="#FFF" />
				</TouchableOpacity>
			</View>

			<TextInput style={styles.iconSearchInput} placeholder="Search icons..." placeholderTextColor="#666" value={searchQuery} onChangeText={setSearchQuery} />

			<ScrollView style={styles.iconGridScrollView}>
				<View style={styles.iconGrid}>
					{filteredIcons.map((iconName) => (
						<TouchableOpacity
							key={iconName}
							style={[styles.iconGridItem, value === iconName && styles.iconGridItemActive]}
							onPress={() => {
								onChange(iconName);
								onClose();
							}}>
							<Ionicons name={iconName} size={24} color="#fff" />
							<Text style={styles.iconName}>{iconName}</Text>
						</TouchableOpacity>
					))}
				</View>
			</ScrollView>
		</View>
	);
};

export const PropertiesPanel = (props) => {
	const theme = useTheme();
	const { fetchDesign, updateDesign } = useDesign();
	const { fetchContent, updateContent } = useContent();
	const { selectedComponent, onPageChange } = useComponentEditor();
	const [activeSection, setActiveSection] = useState(null);
	const [localState, setLocalState] = useState({});
	const [originalState, setOriginalState] = useState({});
	const [isInitialized, setIsInitialized] = useState(false);
	const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
	const [customColorInput, setCustomColorInput] = useState(""); // For custom color input
	const [customColors, setCustomColors] = useState([]); // Store custom colors
	const [isInvalidColor, setIsInvalidColor] = useState(false); // Track invalid color input
	const { onOpenModal } = props; // Receive onOpenModal prop

	// project properties
	const { currentProject } = useProject();
	const [userRoles, setUserRoles] = useState([]);
	const [appServices, setAppServices] = useState([]);

	useEffect(() => {
		if (currentProject) {
			console.log("currentProject", currentProject);
			setUserRoles(currentProject.userRoles);
			setAppServices(currentProject.appServices);
		}
	}, [currentProject]);
	// Predefined color palette - 12 colors (4 empty slots will be filled with custom colors)
	const defaultColorPalette = ["#2A2A2A", "#FFFFFF", "#FF4B4B", "#4BFF4B", "#4B4BFF", "#FFE14B", "#FF4BFF", "#4BFFFF", "#FF914B", "#914BFF", "#4BFF91", "#FF4B91"];

	// Get the full color palette including custom colors
	const getColorPalette = () => {
		// Combine default colors with custom colors, limit to 12 total
		const combined = [...defaultColorPalette];

		// Add custom colors, replacing the oldest ones if we exceed 12
		customColors.forEach((color) => {
			if (!combined.includes(color)) {
				combined.push(color);
			}
		});

		// Return exactly 16 items (12 colors + 4 empty slots)
		return combined.slice(0, 12).concat(Array(4).fill(null));
	};

	// Add a custom color to the palette
	const addCustomColor = (color) => {
		if (!customColors.includes(color)) {
			// Add new color to the beginning so it appears first among custom colors
			setCustomColors((prev) => [color, ...prev].slice(0, 4)); // Keep only the 4 most recent custom colors
		}
	};

	// Handle custom color submission
	const handleCustomColorSubmit = (propertyKey, fontValue) => {
		// Validate the color format
		let colorValue = customColorInput.trim();

		// Add # if missing
		if (colorValue && !colorValue.startsWith("#")) {
			colorValue = `#${colorValue}`;
			setCustomColorInput(colorValue);
		}

		if (colorValue && /^#[0-9A-Fa-f]{8}$/.test(colorValue)) {
			// Add to custom colors
			addCustomColor(colorValue);

			// Update the font color
			const updatedValue = { ...fontValue, color: colorValue };
			handlePropertyChange(propertyKey, updatedValue);
			setOpenDropdown(null);
			setIsInvalidColor(false);
			return true;
		}

		// Show error state
		setIsInvalidColor(true);
		return false;
	};

	// Handle color input change
	const handleColorInputChange = (text) => {
		setCustomColorInput(text);
		setIsInvalidColor(false); // Reset error state when input changes
	};

	// Initialize local state when selected component changes
	useEffect(() => {
		if (!selectedComponent) {
			setLocalState({});
			setOriginalState({});
			setIsInitialized(false);
			return;
		}

		try {
			const componentType = selectedComponent.componentType;
			const currentStyles = fetchDesign(componentType) || {};
			const currentContent = fetchContent(componentType) || {};
			const initialState = {};

			Object.entries(selectedComponent.properties).forEach(([key, prop]) => {
				// Handle dot notation for nested properties
				if (key.includes(".")) {
					const [parentKey, childKey] = key.split(".");

					// Initialize parent object if needed
					if (!initialState[parentKey]) {
						initialState[parentKey] = {};
					}

					switch (prop.store) {
						case "design":
							// For design properties, first check if the parent exists
							if (currentStyles[parentKey]) {
								initialState[parentKey][childKey] = currentStyles[parentKey][childKey] !== undefined ? currentStyles[parentKey][childKey] : prop.value;
							} else {
								initialState[parentKey][childKey] = prop.value !== undefined ? prop.value : {};
							}
							break;
						case "content":
							// For content properties, first check if the parent exists
							if (currentContent[parentKey]) {
								initialState[parentKey][childKey] = currentContent[parentKey][childKey] !== undefined ? currentContent[parentKey][childKey] : prop.value;
							} else {
								initialState[parentKey][childKey] = prop.value !== undefined ? prop.value : {};
							}
							break;
					}
				} else {
					// Handle regular properties
					switch (prop.store) {
						case "design":
							initialState[key] = currentStyles[key] ?? prop.value ?? {};
							break;
						case "content":
							initialState[key] = currentContent[key] ?? prop.value ?? {};
							break;
					}
				}
			});
			setLocalState(initialState);
			setOriginalState(initialState);
			setIsInitialized(true);
		} catch (error) {
			console.error("Error initializing PropertiesPanel state:", error);
			setIsInitialized(false);
		}
	}, [selectedComponent]);

	if (!selectedComponent) {
		return (
			<View style={styles.container}>
				<Text style={styles.emptyText}>Select a component to edit its properties</Text>
			</View>
		);
	}

	if (!isInitialized) {
		return (
			<View style={styles.container}>
				<Text style={styles.emptyText}>Loading component properties...</Text>
			</View>
		);
	}

	let editableProperties = { sections: [], properties: {} };

	try {
		editableProperties = selectedComponent || { sections: [], properties: {} };
	} catch (error) {
		console.error("Error getting editable properties:", error);
	}
	const { sections = [], properties = {} } = editableProperties;

	const handlePropertyChange = (propertyKey, value) => {
		// Deep clone the current state to avoid mutation
		const newState = JSON.parse(JSON.stringify(localState));

		// Handle dot notation for nested properties
		if (propertyKey.includes(".")) {
			const [parentKey, childKey] = propertyKey.split(".");

			// Initialize parent object if it doesn't exist
			if (!newState[parentKey]) {
				newState[parentKey] = {};
			}

			// Set the child property
			newState[parentKey][childKey] = value;
		} else {
			// Regular property
			newState[propertyKey] = value;
		}

		// Update local state
		setLocalState(newState);

		const componentType = selectedComponent.componentType;
		const property = selectedComponent.properties[propertyKey];

		switch (property.store) {
			case "design":
				updateDesign(componentType, {
					[propertyKey]: value,
				});
				break;
			case "content":
				// Handle dot notation for nested properties in content updates
				if (propertyKey.includes(".")) {
					const [parentKey, childKey] = propertyKey.split(".");

					// Get existing content for the component
					const existingContent = fetchContent(componentType) || {};

					// Create a new parent object with existing data plus the update
					const updatedParent = {
						...(existingContent[parentKey] || {}),
						[childKey]: value,
					};

					// Update the content with the new parent object
					updateContent(componentType, {
						[parentKey]: updatedParent,
					});
				} else {
					// Regular update
					updateContent(componentType, {
						[propertyKey]: value,
					});
				}
				break;
		}
	};

	const handleApply = () => {
		// Set original state to current local state
		setOriginalState(localState);

		// Get the component type and other necessary information
		const componentType = selectedComponent.componentType;

		// Create a snapshot of the current state from all three contexts
		const stateSnapshot = {
			design: fetchDesign(componentType),
			content: fetchContent(componentType),
			component: {},
		};

		// Access onPageChange directly from the ComponentEditorContext
		// This will update the unsavedChanges count
		if (selectedComponent && onPageChange) {
			// Get the active page ID

			// Call onPageChange directly from the context
			onPageChange({
				contextStates: stateSnapshot,
			});
		}
	};

	const handleRevert = () => {
		// Set local state back to original state
		setLocalState(originalState);

		// Get the component type
		const componentType = selectedComponent.componentType;

		// Iterate through all properties in originalState
		Object.entries(selectedComponent.properties).forEach(([propertyKey, property]) => {
			// Skip if property doesn't exist
			if (!property) return;

			// Get the value from originalState
			let originalValue;
			if (propertyKey.includes(".")) {
				const [parentKey, childKey] = propertyKey.split(".");
				originalValue = originalState[parentKey] && originalState[parentKey][childKey];
			} else {
				originalValue = originalState[propertyKey];
			}

			// Skip if original value doesn't exist
			if (originalValue === undefined) return;

			// Update the appropriate store based on property type
			switch (property.store) {
				case "design":
					if (propertyKey.includes(".")) {
						const [parentKey, childKey] = propertyKey.split(".");
						const currentDesign = fetchDesign(componentType) || {};
						const updatedParent = {
							...(currentDesign[parentKey] || {}),
							[childKey]: originalValue,
						};
						updateDesign(componentType, {
							[parentKey]: updatedParent,
						});
					} else {
						updateDesign(componentType, {
							[propertyKey]: originalValue,
						});
					}
					break;
				case "content":
					if (propertyKey.includes(".")) {
						const [parentKey, childKey] = propertyKey.split(".");
						const currentContent = fetchContent(componentType) || {};
						const updatedParent = {
							...(currentContent[parentKey] || {}),
							[childKey]: originalValue,
						};
						updateContent(componentType, {
							[parentKey]: updatedParent,
						});
					} else {
						updateContent(componentType, {
							[propertyKey]: originalValue,
						});
					}
					break;
			}
		});

		console.log("Reverted to original state:", originalState); // Debug log
	};

	const hasChanges = () => {
		// Deep compare localState and originalState
		return JSON.stringify(localState) !== JSON.stringify(originalState);
	};

	const renderPropertyEditor = (propertyKey, index) => {
		const property = properties[propertyKey];
		if (!property) return null;

		let { type, label, options, description } = property;

		switch (options) {
			case "userRoles":
				options = userRoles.map((role) => ({ label: role.displayName, group: role.name }));
				break;
			case "appServices":
				options = appServices.map((service) => ({ label: service.displayName, group: service.name }));
				break;
		}
		// Handle dot notation for accessing nested property values
		let value;
		if (propertyKey.includes(".")) {
			const [parentKey, childKey] = propertyKey.split(".");
			// Access nested value if parent exists in local state
			if (localState[parentKey]) {
				value = localState[parentKey][childKey];
			} else {
				// Fallback to property's default value
				value = property.value;
			}
		} else {
			// Get value for regular property
			value = localState[propertyKey] !== undefined ? localState[propertyKey] : property.value;
		}

		switch (type) {
			case "boolean":
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<Switch value={value} onValueChange={(newValue) => handlePropertyChange(propertyKey, newValue)} trackColor={{ false: theme.colors.gray[6], true: theme.colors.primary }} thumbColor={theme.colors.systemBackground.primary} />
					</View>
				);

			case "text":
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<TextInput style={styles.input} value={String(value || "")} onChangeText={(newValue) => handlePropertyChange(propertyKey, newValue)} placeholder={property.placeholder || "Enter text..."} placeholderTextColor="#666" />
					</View>
				);
			case "gemitems": {
				const tabs = value || []; // Ensure value is an array
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							{description && <Text style={styles.propertyDescription}>{description}</Text>}
						</View>
						<View style={styles.tabsControlContainer}>
							<ScrollView style={styles.tabsList}>
								{tabs.map((tab, tabIndex) => (
									<View key={tab.id || tabIndex} style={{ ...styles.tabItem, zIndex: -tabIndex }}>
										<View style={styles.tabItemHeader}>
											{/* Display Label as non-editable text */}
											<Text style={styles.tabItemTitle}>{tab.header || `Gem ${tabIndex + 1}`}</Text>
											<View style={styles.tabItemActions}>
												{/* Edit Button */}
												<TouchableOpacity
													style={styles.editTabButton}
													onPress={() => {
														// Placeholder for edit action
														console.log("Edit gem:", tab.id);
													}}>
													<Ionicons name="create-outline" size={16} color={theme.colors.primary} />
												</TouchableOpacity>
												{/* Delete Button */}
												<TouchableOpacity
													style={styles.deleteTabButton}
													onPress={() => {
														const newTabs = tabs.filter((_, idx) => idx !== tabIndex);
														handlePropertyChange(propertyKey, newTabs); // Use propertyKey here
													}}>
													<Ionicons name="trash-outline" size={16} color={theme.colors.error} />
												</TouchableOpacity>
											</View>
										</View>
										{/* Removed Label Input and Icon Selection */}
									</View>
								))}
							</ScrollView>

							{/* Enable the Add New Gem button and set onPress */}
							<TouchableOpacity
								style={styles.addTabButton} // Use regular style
								onPress={() => {
									if (onOpenModal) {
										onOpenModal(MODAL_MODES.COMPONENT_CREATOR, (newGemData) => {
											// This callback runs when the modal completes
											console.log("New gem data received:", newGemData);
											// Get the current list of gems
											let currentGems = [];
											if (propertyKey.includes(".")) {
												const [parentKey, childKey] = propertyKey.split(".");
												currentGems = localState[parentKey]?.[childKey] || [];
											} else {
												currentGems = localState[propertyKey] || [];
											}

											// Create a new gem object (adjust structure as needed based on newGemData)
											const newGem = {
												id: Date.now().toString(), // Simple ID generation
												...newGemData, // Example: use name from modal data
											};

											// Update the state with the new gem added
											handlePropertyChange(propertyKey, [...currentGems, newGem]);
										});
									}
								}}>
								<Ionicons name="add" size={16} color="#fff" />
								<Text style={styles.addTabButtonText}>Add New Gem</Text>
							</TouchableOpacity>
						</View>
					</View>
				);
			}
			case "dropdown":
				return (
					<View style={{ ...styles.propertyRow, zIndex: 20 - index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<View style={{ position: "relative", width: "100%" }}>
							<TouchableOpacity
								style={styles.dropdownSelect}
								onPress={() => {
									setOpenDropdown(openDropdown === propertyKey ? null : propertyKey);
								}}>
								<Text style={styles.dropdownSelectText}>
									{(() => {
										// Find the matching option
										const matchingOption = Array.isArray(options) ? options.find((opt) => opt.group === value) : null;

										// Return the label of the matching option, or the value itself if no match
										return matchingOption ? matchingOption.label : value !== undefined ? String(value) : "Select...";
									})()}
								</Text>
								<Ionicons name={openDropdown === propertyKey ? "chevron-up" : "chevron-down"} size={16} color="#fff" />
							</TouchableOpacity>

							{openDropdown === propertyKey && (
								<View style={styles.dropdownOptionsList}>
									{options.map((option) => (
										<TouchableOpacity
											key={option.group || option}
											style={[styles.dropdownOption, value === (option.group || option) && styles.dropdownOptionActive]}
											onPress={() => {
												handlePropertyChange(propertyKey, option.group || option);
												setOpenDropdown(null);
											}}>
											<Text style={[styles.dropdownOptionText, value === (option.group || option) && styles.dropdownOptionTextActive]}>{option.label || String(option)}</Text>
										</TouchableOpacity>
									))}
								</View>
							)}
						</View>
					</View>
				);

			case "select":
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<View style={styles.selectContainer}>
							{options.map((option) => (
								<TouchableOpacity key={option} style={[styles.selectOption, value === option && styles.selectOptionActive]} onPress={() => handlePropertyChange(propertyKey, option)}>
									<Text style={[styles.selectOptionText, value === option && styles.selectOptionTextActive]}>{option}</Text>
								</TouchableOpacity>
							))}
						</View>
					</View>
				);

			case "color":
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<View style={styles.colorContainer}>
							<View style={[styles.colorPreview, { backgroundColor: value }]} />
							<TextInput style={styles.colorInput} value={value} onSubmitEditing={(newValue) => handlePropertyChange(propertyKey, newValue)} placeholder="#000000" placeholderTextColor="#666" />
						</View>
					</View>
				);

			case "textarea":
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<TextInput style={[styles.input, styles.textArea]} value={String(value || "")} onSubmitEditing={(newValue) => handlePropertyChange(propertyKey, newValue)} multiline numberOfLines={3} placeholderTextColor="#666" />
					</View>
				);
			case "tabitems":
				const tabs = value || properties.tabs || [];
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							{description && <Text style={styles.propertyDescription}>{description}</Text>}
						</View>
						<View style={styles.tabsControlContainer}>
							<ScrollView style={styles.tabsList}>
								{tabs.map((tab, tabIndex) => (
									<View key={tab.id || tabIndex} style={{ ...styles.tabItem, zIndex: -tabIndex }}>
										<View style={styles.tabItemHeader}>
											<Text style={styles.tabItemTitle}>Tab {tabIndex + 1}</Text>
											<TouchableOpacity
												style={styles.deleteTabButton}
												onPress={() => {
													const newTabs = tabs.filter((_, idx) => idx !== tabIndex);
													handlePropertyChange("tabs", newTabs);
												}}>
												<Ionicons name="trash-outline" size={16} color="#FF3B30" />
											</TouchableOpacity>
										</View>

										{/* Label Input */}
										<View style={styles.tabControl}>
											<View style={styles.tabControlRow}>
												<View style={styles.iconSelectorButton}>
													<TextInput
														style={styles.tabInput}
														value={tab.label || ""}
														onChangeText={(newLabel) => {
															const newTabs = [...tabs];
															newTabs[tabIndex] = { ...tab, label: newLabel };
															handlePropertyChange("tabs", newTabs);
														}}
														placeholder="Tab Label"
														placeholderTextColor="#666"
													/>
												</View>
											</View>
										</View>

										{/* Icon Selection - Modified to use EditorModal */}
										<View style={styles.tabControl}>
											<View style={styles.tabControlRow}>
												<TouchableOpacity
													style={styles.iconSelectorButton}
													onPress={() => {
														if (props.onOpenModal) {
															props.onOpenModal(MODAL_MODES.ICON_PICKER, (selectedIcon) => {
																const newTabs = [...tabs];
																newTabs[tabIndex] = { ...tab, icon: selectedIcon };
																handlePropertyChange("tabs", newTabs);
															});
														}
													}}>
													<View style={styles.iconSelectorLeft}>
														<View style={styles.selectedIconPreview}>
															<FontAwesome6 name={tab.icon || "help-circle-outline"} size={16} color="#fff" />
														</View>
														<Text style={styles.iconSelectorText}>{tab.icon || "Select Icon"}</Text>
													</View>
													<FontAwesome6 name="chevron-right" size={16} color="#fff" />
												</TouchableOpacity>
											</View>
										</View>
									</View>
								))}
							</ScrollView>

							<TouchableOpacity
								style={styles.addTabButton}
								onPress={() => {
									const newTab = {
										id: Date.now().toString(),
										label: "New Tab",
										icon: "document-outline",
									};
									handlePropertyChange("tabs", [...tabs, newTab]);
								}}>
								<Ionicons name="add" size={16} color="#fff" />
								<Text style={styles.addTabButtonText}>Add New Tab</Text>
							</TouchableOpacity>
						</View>
					</View>
				);
			case "tabs":
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							{description && <Text style={styles.propertyDescription}>{description}</Text>}
						</View>
						<View style={styles.tabsSharedStylesContainer}>
							{/* Visibility Controls */}
							<View style={styles.tabControl}>
								<Text style={styles.tabControlLabel}>Visibility</Text>
								<View style={styles.visibilityControls}>
									<View style={styles.visibilityControl}>
										<Text style={styles.visibilityLabel}>Show Icon</Text>
										<Switch
											value={value.showIcon}
											onValueChange={(newValue) => {
												const updatedValue = { ...value, showIcon: newValue };
												if (!newValue && !value.showLabel) {
													updatedValue.showLabel = true;
												}
												handlePropertyChange(propertyKey, updatedValue);
											}}
											trackColor={{ false: theme.colors.gray[6], true: theme.colors.primary }}
											thumbColor={theme.colors.systemBackground.primary}
										/>
									</View>
									<View style={styles.visibilityControl}>
										<Text style={styles.visibilityLabel}>Show Label</Text>
										<Switch
											value={value.showLabel}
											onValueChange={(newValue) => {
												const updatedValue = { ...value, showLabel: newValue };
												if (!newValue && !value.showIcon) {
													updatedValue.showIcon = true;
												}
												handlePropertyChange(propertyKey, updatedValue);
											}}
											trackColor={{ false: theme.colors.gray[6], true: theme.colors.primary }}
											thumbColor={theme.colors.systemBackground.primary}
										/>
									</View>
								</View>
							</View>

							{/* Font Style */}
							<View style={{ ...styles.tabControl, zIndex: -1 }}>
								<Text style={styles.tabControlLabel}>Font Style</Text>
								<View style={styles.tabFontControls}>
									<View style={styles.tabFontRow}>
										<View style={styles.tabFontFamily}>
											<TouchableOpacity
												style={[styles.fontFamilyButton, styles.dropdownButton]}
												onPress={() => {
													setOpenDropdown(openDropdown === `${propertyKey}-family` ? null : `${propertyKey}-family`);
												}}>
												<Text style={styles.fontControlText}>{value.fontFamily || "Helvetica"}</Text>
												<Ionicons name={openDropdown === `${propertyKey}-family` ? "chevron-up" : "chevron-down"} size={12} color="#fff" />
											</TouchableOpacity>
											{openDropdown === `${propertyKey}-family` && (
												<View style={[styles.dropdownMenu, styles.fontFamilyDropdown]}>
													{["System", "SF Pro", "Helvetica", "Arial"].map((family) => (
														<TouchableOpacity
															key={family}
															style={[styles.dropdownItem, value.fontFamily === family && styles.dropdownItemActive]}
															onPress={() => {
																const updatedValue = {
																	...value,
																	fontFamily: family,
																};
																handlePropertyChange(propertyKey, updatedValue);
																setOpenDropdown(null);
															}}>
															<Text style={[styles.dropdownItemText, value.fontFamily === family && styles.dropdownItemTextActive]}>{family}</Text>
														</TouchableOpacity>
													))}
												</View>
											)}
										</View>
										<View style={styles.tabFontWeight}>
											<TouchableOpacity
												style={[styles.fontWeightButton, styles.dropdownButton]}
												onPress={() => {
													setOpenDropdown(openDropdown === `${propertyKey}-weight` ? null : `${propertyKey}-weight`);
												}}>
												<Text style={styles.fontControlText}>{value.fontWeight || "400"}</Text>
												<Ionicons name={openDropdown === `${propertyKey}-weight` ? "chevron-up" : "chevron-down"} size={12} color="#fff" />
											</TouchableOpacity>
											{openDropdown === `${propertyKey}-weight` && (
												<View style={[styles.dropdownMenu, styles.weightDropdownMenu]}>
													{["400", "500", "600", "700"].map((weight) => (
														<TouchableOpacity
															key={weight}
															style={[styles.dropdownItem, value.fontWeight === weight && styles.dropdownItemActive]}
															onPress={() => {
																const updatedValue = {
																	...value,
																	fontWeight: weight,
																};
																handlePropertyChange(propertyKey, updatedValue);
																setOpenDropdown(null);
															}}>
															<Text style={[styles.dropdownItemText, value.fontWeight === weight && styles.dropdownItemTextActive]}>{weight}</Text>
														</TouchableOpacity>
													))}
												</View>
											)}
										</View>
									</View>
									<View style={{ ...styles.tabFontRow, zIndex: -1 }}>
										<View style={styles.tabFontSize}>
											<TouchableOpacity
												style={styles.tabFontSizeButton}
												onPress={() => {
													const newSize = Math.max(8, (value.fontSize || 14) - 1);
													const updatedValue = {
														...value,
														fontSize: newSize,
													};
													handlePropertyChange(propertyKey, updatedValue);
												}}>
												<Ionicons name="remove" size={16} color="#fff" />
											</TouchableOpacity>
											<TextInput
												style={styles.tabFontSizeInput}
												value={String(value.fontSize || 14)}
												onSubmitEditing={(newSize) => {
													const size = parseInt(newSize) || 14;
													const updatedValue = {
														...value,
														fontSize: size,
													};
													handlePropertyChange(propertyKey, updatedValue);
												}}
												keyboardType="numeric"
											/>
											<TouchableOpacity
												style={styles.tabFontSizeButton}
												onPress={() => {
													const newSize = Math.min(72, (value.fontSize || 14) + 1);
													const updatedValue = {
														...value,
														fontSize: newSize,
													};
													handlePropertyChange(propertyKey, updatedValue);
												}}>
												<Ionicons name="add" size={16} color="#fff" />
											</TouchableOpacity>
										</View>
										<TouchableOpacity style={[styles.tabFontColor, { backgroundColor: value.color || "#FFFFFF" }]} onPress={() => setOpenDropdown(`${propertyKey}-color`)} />
										{openDropdown === `${propertyKey}-color` && (
											<View style={[styles.dropdownMenu, styles.colorPickerDropdown]}>
												<View style={styles.colorGrid}>
													{["#FFFFFF", "#000000", "#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF"].map((color) => (
														<TouchableOpacity
															key={color}
															style={[styles.colorSwatch, { backgroundColor: color }, value.color === color && styles.colorSwatchActive]}
															onPress={() => {
																const updatedValue = {
																	...value,
																	color,
																};
																handlePropertyChange(propertyKey, updatedValue);
																setOpenDropdown(null);
															}}
														/>
													))}
												</View>
											</View>
										)}
									</View>
								</View>
							</View>

							{/* Background Color */}
							<View style={{ ...styles.tabControl, zIndex: -2 }}>
								<View style={styles.tabControlRow}>
									<Text style={{ ...styles.tabControlLabel, flex: 1 }}>Background Color</Text>
									<TouchableOpacity style={[styles.colorButton, { backgroundColor: value.backgroundColor || "#2A2A2A" }]} onPress={() => setOpenDropdown(`${propertyKey}-bg`)} />
									{openDropdown === `${propertyKey}-bg` && (
										<View style={[styles.dropdownMenu, styles.colorPickerDropdown]}>
											<ScrollView style={styles.colorGridContainer}>
												<View style={styles.colorGrid}>
													{getColorPalette().map((color, index) =>
														color ? (
															<TouchableOpacity
																key={color}
																style={[styles.colorSwatch, { backgroundColor: color }, value.backgroundColor === color && styles.colorSwatchActive]}
																onPress={() => {
																	const updatedValue = { ...value, backgroundColor: color };
																	handlePropertyChange(propertyKey, updatedValue);
																	setOpenDropdown(null);
																}}
															/>
														) : (
															<View key={`empty-${index}`} style={styles.emptyColorSwatch} />
														)
													)}
												</View>
											</ScrollView>
											<View style={styles.customColorContainer}>
												<TextInput
													style={[styles.customColorInput, isInvalidColor && styles.invalidColorInput]}
													value={customColorInput}
													placeholder="#RRGGBBAA"
													placeholderTextColor="#666"
													onChangeText={handleColorInputChange}
													onSubmitEditing={() => {
														if (handleCustomColorSubmit(propertyKey, { backgroundColor: customColorInput })) {
															const updatedValue = { ...value, backgroundColor: customColorInput };
															handlePropertyChange(propertyKey, updatedValue);
														}
													}}
													returnKeyType="done"
												/>
												<TouchableOpacity
													style={styles.customColorButton}
													onPress={() => {
														if (handleCustomColorSubmit(propertyKey, { backgroundColor: customColorInput })) {
															const updatedValue = { ...value, backgroundColor: customColorInput };
															handlePropertyChange(propertyKey, updatedValue);
														}
													}}>
													<Ionicons name="add" size={16} color="#fff" />
												</TouchableOpacity>
											</View>
										</View>
									)}
								</View>
							</View>
						</View>
					</View>
				);

			case "font":
				// Get font value from local state or property definition
				const fontValue = value || property.value || { fontFamily: "Helvetica", fontSize: 14, color: "#000000", fontWeight: "400" };
				const fontFamilies = ["System", "SF Pro", "Helvetica", "Arial"];
				const fontWeights = ["400", "500", "600", "700"];

				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							{description && <Text style={styles.propertyDescription}>{description}</Text>}
						</View>
						<View style={styles.fontControlContainer}>
							<View style={styles.fontPreview}>
								<Text
									style={[
										styles.fontPreviewText,
										{
											fontFamily: fontValue.fontFamily,
											fontSize: fontValue.fontSize,
											color: fontValue.color,
											fontWeight: fontValue.fontWeight,
										},
									]}>
									Aa
								</Text>
							</View>
							<View style={styles.fontControls}>
								<View style={styles.fontControlRow}>
									<View style={{ position: "relative", flex: 1 }}>
										<TouchableOpacity
											style={[styles.fontFamilyButton, styles.dropdownButton]}
											onPress={() => {
												setOpenDropdown(openDropdown === `${propertyKey}-family` ? null : `${propertyKey}-family`);
											}}>
											<Text style={styles.fontControlText}>{fontValue.fontFamily}</Text>
											<Ionicons name={openDropdown === `${propertyKey}-family` ? "chevron-up" : "chevron-down"} size={12} color="#fff" />
										</TouchableOpacity>
										{openDropdown === `${propertyKey}-family` && (
											<View style={[styles.dropdownMenu, styles.fontFamilyDropdown]}>
												{fontFamilies.map((family) => (
													<TouchableOpacity
														key={family}
														style={[styles.dropdownItem, fontValue.fontFamily === family && styles.dropdownItemActive]}
														onPress={() => {
															const updatedValue = { ...fontValue, fontFamily: family };
															handlePropertyChange(propertyKey, updatedValue);
															setOpenDropdown(null);
														}}>
														<Text style={[styles.dropdownItemText, fontValue.fontFamily === family && styles.dropdownItemTextActive]}>{family}</Text>
													</TouchableOpacity>
												))}
											</View>
										)}
									</View>
									<View style={{ position: "relative" }}>
										<TouchableOpacity
											style={[styles.fontWeightButton, styles.dropdownButton]}
											onPress={() => {
												setOpenDropdown(openDropdown === `${propertyKey}-weight` ? null : `${propertyKey}-weight`);
											}}>
											<Text style={styles.fontControlText}>{fontValue.fontWeight}</Text>
											<Ionicons name={openDropdown === `${propertyKey}-weight` ? "chevron-up" : "chevron-down"} size={12} color="#fff" />
										</TouchableOpacity>
										{openDropdown === `${propertyKey}-weight` && (
											<View style={[styles.dropdownMenu, styles.weightDropdownMenu]}>
												{fontWeights.map((weight) => (
													<TouchableOpacity
														key={weight}
														style={[styles.dropdownItem, fontValue.fontWeight === weight && styles.dropdownItemActive]}
														onPress={() => {
															const updatedValue = { ...fontValue, fontWeight: weight };
															handlePropertyChange(propertyKey, updatedValue);
															setOpenDropdown(null);
														}}>
														<Text style={[styles.dropdownItemText, fontValue.fontWeight === weight && styles.dropdownItemTextActive]}>{weight}</Text>
													</TouchableOpacity>
												))}
											</View>
										)}
									</View>
								</View>
								<View style={{ ...styles.fontControlRow, zIndex: -1 }}>
									<View style={styles.fontSizeControl}>
										<TouchableOpacity
											style={styles.fontSizeButton}
											onPress={() => {
												const newSize = Math.max(8, fontValue.fontSize - 1);
												const updatedValue = { ...fontValue, fontSize: newSize };
												handlePropertyChange(propertyKey, updatedValue);
											}}>
											<Ionicons name="remove" size={12} color="#fff" />
										</TouchableOpacity>
										<TextInput
											style={styles.fontSizeInput}
											value={String(fontValue.fontSize)}
											onSubmitEditing={(newSize) => {
												console.log("Font size change:", newSize);
												const updatedValue = { ...fontValue, fontSize: Number(newSize) };
												handlePropertyChange(propertyKey, updatedValue);
											}}
											keyboardType="numeric"
										/>
										<TouchableOpacity
											style={styles.fontSizeButton}
											onPress={() => {
												const newSize = Math.min(72, fontValue.fontSize + 1);
												const updatedValue = { ...fontValue, fontSize: newSize };
												handlePropertyChange(propertyKey, updatedValue);
											}}>
											<Ionicons name="add" size={12} color="#fff" />
										</TouchableOpacity>
									</View>
									<View style={{ position: "relative" }}>
										<TouchableOpacity
											style={[styles.colorButton, { backgroundColor: fontValue.color }]}
											onPress={() => {
												setOpenDropdown(openDropdown === `${propertyKey}-color` ? null : `${propertyKey}-color`);
												setCustomColorInput(fontValue.color);
											}}
										/>
										{openDropdown === `${propertyKey}-color` && (
											<View style={[styles.dropdownMenu, styles.colorPickerDropdown]}>
												<ScrollView style={styles.colorGridContainer}>
													<View style={styles.colorGrid}>
														{getColorPalette().map((color, index) =>
															color ? (
																<TouchableOpacity
																	key={color}
																	style={[styles.colorSwatch, { backgroundColor: color }, fontValue.color === color && styles.colorSwatchActive]}
																	onPress={() => {
																		const updatedValue = { ...fontValue, color: color };
																		handlePropertyChange(propertyKey, updatedValue);
																		setOpenDropdown(null);
																	}}
																/>
															) : (
																<View key={`empty-${index}`} style={styles.emptyColorSwatch} />
															)
														)}
													</View>
												</ScrollView>
												<View style={styles.customColorContainer}>
													<TextInput
														style={[styles.customColorInput, isInvalidColor && styles.invalidColorInput]}
														value={customColorInput}
														placeholder="#RRGGBB"
														placeholderTextColor="#666"
														onSubmitEditing={() => handleCustomColorSubmit(propertyKey, { color: customColorInput })}
														returnKeyType="done"
													/>
													<TouchableOpacity style={styles.customColorButton} onPress={() => handleCustomColorSubmit(propertyKey, { color: customColorInput })}>
														<Ionicons name="add" size={16} color="#fff" />
													</TouchableOpacity>
												</View>
											</View>
										)}
									</View>
								</View>
							</View>
						</View>
					</View>
				);

			case "group":
				// Get group value from local state or property definition

				// Regular group handling
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							{description && <Text style={styles.propertyDescription}>{description}</Text>}
						</View>
						<View style={styles.groupContainer}>
							{Object.entries(property.properties || {}).map(([key, prop]) => {
								if (!prop) return null;

								// Get nested value from local state or property definition
								const nestedValue = value[key] !== undefined ? value[key] : prop.value;

								return (
									<View key={key} style={styles.groupItem}>
										<Text style={styles.groupItemLabel}>{prop.label}</Text>
										<TextInput
											style={styles.groupItemInput}
											value={String(nestedValue || "")}
											onChangeText={(newValue) => {
												const updatedvalue = { ...value };
												updatedvalue[key] = prop.type === "number" ? Number(newValue) : newValue;
												handlePropertyChange(propertyKey, updatedvalue);
											}}
											keyboardType={prop.type === "number" ? "numeric" : "default"}
										/>
									</View>
								);
							})}
						</View>
					</View>
				);

			case "icon":
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<View style={{ width: "100%" }}>
							{/* Replace TouchableOpacity with one that opens the modal */}
							<TouchableOpacity
								style={styles.iconSelectorButton} // Use the same style as tabitems
								onPress={() => {
									if (props.onOpenModal) {
										props.onOpenModal(MODAL_MODES.ICON_PICKER, (selectedIcon) => {
											handlePropertyChange(propertyKey, selectedIcon);
										});
									}
								}}>
								<View style={styles.iconSelectorLeft}>
									<View style={styles.selectedIconPreview}>
										{/* Use FontAwesome6 or Ionicons based on what's common */}
										<FontAwesome6 name={value || "help-circle-outline"} size={16} color="#fff" />
									</View>
									<Text style={styles.iconSelectorText}>{value || "Select Icon"}</Text>
								</View>
								<FontAwesome6 name="chevron-right" size={16} color="#fff" />
							</TouchableOpacity>
						</View>
					</View>
				);

			default: // text, number
				return (
					<View style={{ ...styles.propertyRow, zIndex: -index }} key={propertyKey}>
						<View style={styles.propertyLabelContainer}>
							<Text style={styles.propertyLabel}>{label}</Text>
							<Text style={styles.propertyDescription}>{description}</Text>
						</View>
						<TextInput
							style={styles.input}
							value={String(value || "")}
							onSubmitEditing={(newValue) => handlePropertyChange(propertyKey, type === "number" ? Number(newValue) : newValue)}
							keyboardType={type === "number" ? "numeric" : "default"}
							placeholderTextColor="#666"
						/>
					</View>
				);
		}
	};

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<View style={styles.headerTop}>
					<Text style={styles.componentTitle}>{selectedComponent.componentType || "Component"}</Text>
					{hasChanges() && (
						<View style={styles.headerButtons}>
							<TouchableOpacity style={styles.headerButton} onPress={handleRevert}>
								<Text style={styles.headerButtonText}>Revert</Text>
							</TouchableOpacity>
							<TouchableOpacity style={[styles.headerButton, styles.headerButtonPrimary]} onPress={handleApply}>
								<Text style={[styles.headerButtonText, styles.headerButtonTextPrimary]}>Apply</Text>
							</TouchableOpacity>
						</View>
					)}
				</View>
			</View>

			<ScrollView style={styles.content}>
				{sections.map((section, index) => (
					<View key={section.id} style={[styles.section, { zIndex: -index }]}>
						<TouchableOpacity style={styles.sectionHeader} onPress={() => setActiveSection(activeSection === section.id ? null : section.id)}>
							<Text style={styles.sectionTitle}>{section.title}</Text>
							<Ionicons name={activeSection === section.id ? "chevron-down" : "chevron-forward"} size={16} color="#fff" />
						</TouchableOpacity>

						{activeSection === section.id && <View style={styles.sectionContent}>{section.properties.map((propertyKey, index) => renderPropertyEditor(propertyKey, index))}</View>}
					</View>
				))}
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		width: 280,
		backgroundColor: "#1A1A1A",
		borderLeftWidth: 1,
		borderLeftColor: "#333",
		height: "100%",
	},
	header: {
		padding: 16,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	headerTop: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
	},
	headerButtons: {
		flexDirection: "row",
		gap: 8,
	},
	headerButton: {
		paddingVertical: 4,
		paddingHorizontal: 8,
		borderRadius: 4,
		backgroundColor: "#2A2A2A",
	},
	headerButtonPrimary: {
		backgroundColor: "#007AFF",
	},
	headerButtonText: {
		color: "#fff",
		fontSize: 12,
	},
	headerButtonTextPrimary: {
		fontWeight: "600",
	},
	componentTitle: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	content: {
		flex: 1,
	},
	section: {
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	sectionHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: 16,
	},
	sectionTitle: {
		color: "#fff",
		fontSize: 13,
		fontWeight: "500",
	},
	sectionContent: {
		padding: 16,
		paddingTop: 0,
	},
	propertyRow: {
		marginBottom: 16,
	},
	propertyLabelContainer: {
		marginBottom: 8,
	},
	propertyLabel: {
		color: "#fff",
		fontSize: 12,
		marginBottom: 4,
	},
	propertyDescription: {
		color: "#666",
		fontSize: 11,
	},
	input: {
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		padding: 8,
		color: "#fff",
		fontSize: 12,
	},
	textArea: {
		minHeight: 80,
		textAlignVertical: "top",
	},
	selectContainer: {
		flexDirection: "row",
		gap: 8,
	},
	selectOption: {
		backgroundColor: "#2A2A2A",
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 4,
	},
	selectOptionActive: {
		backgroundColor: "#3A3A3A",
	},
	selectOptionText: {
		color: "#fff",
		fontSize: 12,
	},
	selectOptionTextActive: {
		color: "#fff",
		fontWeight: "600",
	},
	emptyText: {
		color: "#666",
		textAlign: "center",
		marginTop: 20,
	},
	colorContainer: {
		flexDirection: "row",
		alignItems: "center",
	},
	colorPreview: {
		width: 24,
		height: 24,
		borderRadius: 4,
		marginRight: 8,
	},
	colorInput: {
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		padding: 8,
		color: "#fff",
		fontSize: 12,
	},
	fontControlContainer: {
		backgroundColor: "#2A2A2A",
		borderRadius: 8,
		padding: 12,
		gap: 12,
	},
	fontPreview: {
		backgroundColor: "#FFFFFF",
		borderRadius: 6,
		padding: 8,
		alignItems: "center",
	},
	fontPreviewText: {
		fontSize: 16,
	},
	fontControls: {
		gap: 8,
	},
	fontControlRow: {
		flexDirection: "row",
		gap: 8,
	},
	fontFamilyButton: {
		flex: 1,
		position: "relative",
	},
	fontWeightButton: {
		width: 60,
		position: "relative",
	},
	dropdownButton: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 8,
	},
	fontControlText: {
		color: "#fff",
		fontSize: 12,
		flex: 1,
		marginRight: 8,
	},
	fontSizeControl: {
		flex: 1,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 4,
	},
	fontSizeButton: {
		width: 24,
		height: 24,
		borderRadius: 4,
		backgroundColor: "#1A1A1A",
		justifyContent: "center",
		alignItems: "center",
	},
	fontSizeInput: {
		width: 50,
		color: "#fff",
		fontSize: 12,
		textAlign: "center",
	},
	colorButton: {
		width: 60,
		height: 32,
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "#1A1A1A",
	},
	colorPickerDropdown: {
		width: 180,
		right: 0,
		left: "auto",
		padding: 8,
	},
	colorGridContainer: {
		maxHeight: 120,
	},
	colorGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		justifyContent: "space-between",
	},
	colorSwatch: {
		width: 30,
		height: 30,
		margin: 4,
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "#333",
	},
	colorSwatchActive: {
		borderColor: "#fff",
		borderWidth: 2,
	},
	customColorContainer: {
		flexDirection: "row",
		marginTop: 8,
		alignItems: "center",
	},
	customColorInput: {
		flex: 1,
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 8,
		color: "#fff",
		fontSize: 12,
		marginRight: 8,
	},
	invalidColorInput: {
		borderWidth: 1,
		borderColor: "#FF3B30",
	},
	customColorButton: {
		width: 32,
		height: 32,
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		justifyContent: "center",
		alignItems: "center",
	},
	groupContainer: {
		gap: 8,
	},
	groupItem: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		padding: 8,
	},
	groupItemLabel: {
		color: "#fff",
		fontSize: 12,
	},
	groupItemInput: {
		width: 120,
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 6,
		color: "#fff",
		fontSize: 12,
		textAlign: "right",
	},
	dropdownMenu: {
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		marginTop: 4,
		zIndex: 10,
		borderWidth: 1,
		borderColor: "#333",
		maxHeight: 150,
		overflow: "hidden",
	},
	weightDropdownMenu: {
		width: 60,
	},
	dropdownItem: {
		padding: 8,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	dropdownItemActive: {
		backgroundColor: "#3A3A3A",
	},
	dropdownItemText: {
		color: "#fff",
		fontSize: 12,
	},
	dropdownItemTextActive: {
		fontWeight: "600",
	},
	emptyColorSwatch: {
		width: 30,
		height: 30,
		margin: 4,
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "#333",
		backgroundColor: "transparent",
	},
	tabsControlContainer: {
		flexDirection: "column",
		gap: 8,
	},
	tabsList: {
		maxHeight: 400,
		overflow: "visible",
	},
	tabItem: {
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		padding: 8,
		marginBottom: 8,
	},
	tabItemHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 4,
	},
	tabItemTitle: {
		color: "#fff",
		fontSize: 12,
		fontWeight: "500",
		flexShrink: 1, // Allow text to shrink if needed
		marginRight: 8, // Add margin to prevent overlap with buttons
	},
	deleteTabButton: {
		padding: 4,
		borderRadius: 4,
	},
	// Add style for the edit button
	editTabButton: {
		padding: 4,
		borderRadius: 4,
		marginRight: 4, // Space between edit and delete
	},
	// Add style for button container
	tabItemActions: {
		flexDirection: "row",
		alignItems: "center",
	},
	tabControl: {
		marginBottom: 8,
	},
	tabControlLabel: {
		color: "#fff",
		fontSize: 12,
		fontWeight: "500",
		width: 60,
	},
	tabControlRow: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	tabControlInputContainer: {
		flex: 1,
	},
	iconSelector: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: "#2A2A2A",
		padding: 10,
		borderRadius: 4,
		marginBottom: 4,
	},
	selectedIconPreview: {
		width: 28,
		height: 28,
		borderRadius: 4,
		backgroundColor: "#1A1A1A",
		alignItems: "center",
		justifyContent: "center",
		marginRight: 8,
	},
	iconSelectorText: {
		color: "#fff",
		fontSize: 12,
		flex: 1,
	},
	iconDropdownContainer: {
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		marginTop: 4,
		zIndex: 1000,
		borderWidth: 1,
		borderColor: "#333",
		maxHeight: 300,
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.3,
		shadowRadius: 4,
	},
	iconSelectorContainer: {
		padding: 12,
	},
	iconSelectorHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 12,
	},
	iconSelectorTitle: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "500",
	},
	iconSelectorCloseBtn: {
		padding: 4,
	},
	iconSearchInput: {
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 8,
		color: "#fff",
		fontSize: 12,
		marginBottom: 12,
	},
	iconGridScrollView: {
		maxHeight: 220,
	},
	iconGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		justifyContent: "space-between",
	},
	iconGridItem: {
		width: "30%",
		marginBottom: 12,
		padding: 8,
		borderRadius: 4,
		backgroundColor: "#1A1A1A",
		alignItems: "center",
	},
	iconGridItemActive: {
		backgroundColor: "#007AFF",
	},
	iconName: {
		color: "#ccc",
		fontSize: 9,
		marginTop: 4,
		textAlign: "center",
	},
	visibilityControls: {
		flexDirection: "row",
		gap: 8,
	},
	visibilityControl: {
		flexDirection: "row",
		alignItems: "center",
		gap: 8,
	},
	visibilityLabel: {
		color: "#fff",
		fontSize: 12,
	},
	tabsSharedStylesContainer: {
		flexDirection: "column",
		gap: 8,
	},
	tabInput: {
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 8,
		color: "#fff",
		fontSize: 12,
		flex: 1,
	},
	dropdownSelect: {
		backgroundColor: "#2A2A2A",
		padding: 10,
		borderRadius: 4,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 4,
		width: "100%",
	},
	dropdownSelectText: {
		color: "#fff",
		fontSize: 12,
		flex: 1,
	},
	dropdownOptionsList: {
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		marginTop: 0,
		zIndex: 100,
		borderWidth: 1,
		borderColor: "#333",
		maxHeight: 150,
		overflow: "scroll",
		shadowColor: "#000",
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.3,
		shadowRadius: 4,
		elevation: 5,
	},
	dropdownOption: {
		padding: 10,
		borderBottomWidth: 1,
		borderBottomColor: "#333",
	},
	dropdownOptionActive: {
		backgroundColor: "#3A3A3A",
	},
	dropdownOptionText: {
		color: "#fff",
		fontSize: 12,
	},
	dropdownOptionTextActive: {
		fontWeight: "600",
	},
	iconGridContainer: {
		maxHeight: 120,
	},
	fontFamilyDropdown: {
		width: 180,
		right: 0,
		left: "auto",
		padding: 8,
	},
	tabFontControls: {
		backgroundColor: "#1A1A1A",
		borderRadius: 4,
		padding: 8,
		gap: 8,
	},
	tabFontRow: {
		flexDirection: "row",
		gap: 8,
	},
	tabFontFamily: {
		flex: 1,
		position: "relative",
	},
	tabFontWeight: {
		width: 60,
		position: "relative",
	},
	tabFontWeightButton: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		padding: 8,
		borderRadius: 4,
		backgroundColor: "#2A2A2A",
	},
	tabFontSize: {
		flex: 1,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		gap: 8,
		padding: 4,
		borderRadius: 4,
		backgroundColor: "#2A2A2A",
	},
	tabFontSizeButton: {
		width: 24,
		height: 24,
		borderRadius: 4,
		backgroundColor: "#1A1A1A",
		justifyContent: "center",
		alignItems: "center",
	},
	tabFontSizeInput: {
		width: 50,
		color: "#fff",
		fontSize: 12,
		textAlign: "center",
	},
	tabFontColor: {
		width: 60,
		height: 32,
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "#333",
	},
	addTabButton: {
		padding: 8,
		borderRadius: 4,
		backgroundColor: "#1A1A1A",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		marginTop: 4,
	},
	addTabButtonText: {
		color: "#fff",
		fontSize: 12,
		fontWeight: "500",
	},
	// Add styles for disabled button
	addTabButtonDisabled: {
		backgroundColor: "#1A1A1A",
		opacity: 0.5,
	},
	addTabButtonTextDisabled: {
		color: "#666",
	},
	iconSelectorButton: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		backgroundColor: "#1a1a1a",
		padding: 8,
		paddingRight: 12,
		borderRadius: 4,
		width: "100%",
	},
	iconSelectorLeft: {
		flexDirection: "row",
		alignItems: "center",
		flex: 1,
	},
	selectedIconPreview: {
		width: 28,
		height: 28,
		borderRadius: 4,
		backgroundColor: "#1A1A1A",
		alignItems: "center",
		justifyContent: "center",
		marginRight: 8,
	},
	iconSelectorText: {
		color: "#fff",
		fontSize: 12,
		flex: 1,
	},
});
